import queryString from 'query-string'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

// =============================================================================
// Thunks for the voice-agents /agent-connector-credentials API (AVR-463).
//
// Credentials are stored 1:1 in crm_credentials (reused vault) + a
// voice_agents.agent_connector_credentials extension. Admin org (org 1) only.
//
// These return the API payload directly (no redux store writes) — the page owns
// local state, keeping decrypted secret fields out of the global store.
// fetchingAPI passes `body` straight to fetch(), so bodies are JSON.stringify'd.
// =============================================================================

const baseUrl = () => `${apiService.voiceAgents}/agent-connector-credentials`

// Which fields each connector takes — owned and served by voice-agents (AVR-620).
// Static per deploy, so the promise is memoised and shared by every caller; a
// failure clears it so the next mount retries instead of caching the error.
// The memo is deliberately NOT keyed by org: the spec comes from a code-level
// table (CONNECTOR_SPECS) and the endpoint takes no org param, so it is the
// same for every tenant. If it ever becomes org-varying, key this by org —
// an unkeyed memo would serve the previous org's spec after a switch.
let registryPromise = null
export const fetchConnectorRegistry = () => async (dispatch) => {
  if (!registryPromise) {
    registryPromise = fetchingAPI(`${baseUrl()}/registry`, 'GET', dispatch).catch((error) => {
      registryPromise = null
      throw error
    })
  }
  return registryPromise
}

const withOrg = (params, orgId) => ({
  ...params,
  requested_organization_id: orgId || undefined,
})

const query = (params) => {
  const qs = queryString.stringify(params)
  return qs ? `?${qs}` : ''
}

// List connector-credential metadata (never includes secret fields). Scoped to
// a single agent: pass voiceAgentId so the tab shows only THIS agent's
// credentials — credentials are per-agent (one pair per connector per agent),
// never shared org-wide across agents.
export const fetchConnectorCredentials =
  ({ orgId, connector, voiceAgentId } = {}) =>
  async (dispatch) => {
    const url = `${baseUrl()}${query(
      withOrg(
        { connector: connector || undefined, voice_agent_id: voiceAgentId || undefined },
        orgId
      )
    )}`
    return fetchingAPI(url, 'GET', dispatch)
  }

// Create. body = { connector, voice_agent_id?, auth_type, fields, provider_metadata?,
// provider?, external_account_id? }.
export const createConnectorCredential = (body, orgId) => async (dispatch) => {
  const url = `${baseUrl()}${query(withOrg({}, orgId))}`
  const created = await fetchingAPI(url, 'POST', dispatch, JSON.stringify(body))
  toast.success('Credential created')
  return created
}

// Reveal (decrypt) a secret. Audited server-side — only on explicit action.
// Pass `field` to fetch ONLY that one field's value (minimal exposure — the UI
// copies a single field to the clipboard at a time). Omit it to get all fields.
export const revealConnectorCredential = (id, orgId, field) => async (dispatch) => {
  const url = `${baseUrl()}/${id}/reveal${query(withOrg({ field: field || undefined }, orgId))}`
  return fetchingAPI(url, 'GET', dispatch)
}

// Patch. body may contain { fields (rotate), provider_metadata, active, auth_type }.
export const updateConnectorCredential = (id, body, orgId) => async (dispatch) => {
  const url = `${baseUrl()}/${id}${query(withOrg({}, orgId))}`
  const updated = await fetchingAPI(url, 'PATCH', dispatch, JSON.stringify(body))
  toast.success('Credential updated')
  return updated
}

// Delete the vault row (extension cascades).
export const deleteConnectorCredential = (id, orgId) => async (dispatch) => {
  const url = `${baseUrl()}/${id}${query(withOrg({}, orgId))}`
  await fetchingAPI(url, 'DELETE', dispatch)
  toast.success('Credential deleted')
}
